import type { PartialOptions } from "overlayscrollbars";

import { ScopeType } from "sockapi/scope-types";
import { SockClient } from "sockapi/client/SockClient";

export const defaultOverlayScrollbarsOptions: PartialOptions = {
  showNativeOverlaidScrollbars: true,
  scrollbars: { theme: "os-theme-dark" },
};

/**
 * clsObj({
 *   "puzzle-card": true,
 *   "solved": false,
 *   "active": true,
 * }) // => "puzzle-card active"
 */
export function clsObj(clsObj: Record<string, boolean>): string {
  return Object.entries(clsObj)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .join(" ");
}

export function displayNameOrder(name1: string, name2: string): number {
  const parts1 = name1.replace(/^\[\w+\] /, "").split(/(\d+)/);
  const parts2 = name2.replace(/^\[\w+\] /, "").split(/(\d+)/);
  for (let i = 0; i < parts1.length || i < parts2.length; ++i) {
    if (i >= parts1.length) return -1;
    if (i >= parts2.length) return 1;
    const part1 = parts1[i];
    const part2 = parts2[i];
    if (part1 === part2) continue;
    if (i % 2) return +part1 - +part2;
    return part1.localeCompare(part2);
  }
  return 0;
}

// TODO: This is necessary because stale cached scope may
// cause the client to think something doesn't exist when
// in fact it was newly created. It would be nicer to just
// track when scope state is stale and have a way to wait
// for acked states.
export const addFlushSockAPIMessagesEffect = (
  sockClient: SockClient,
  onDone: () => void
) => {
  return sockClient.addAuthEffect(() => {
    let isCanceled = false;
    (async () => {
      // HACK: Requests are serialized, so after getting a
      // response for the first step, we should have expected
      // to get responses for all earlier subscription.
      // Send another one in case there are subscriptions after
      // this, and a third one just to be safe in case there's
      // some race condition with React rerenders. If we get the
      // team state during this period, a rerender will cancel
      // the effect.
      for (let i = 0; i < 3; i++) {
        const resp = await sockClient.sendDummyScopeStepAndWaitAsync();
        // The step may fail if we get disconnected. If this happens,
        // rely on the auth effect to retrigger when we reconnect.
        if (resp === null || isCanceled) return;
      }

      onDone();
    })().catch(console.error);
    return () => {
      isCanceled = true;
    };
  });
};

export const downloadTeamStateAsync = async (
  sockClient: SockClient,
  teamId: string,
  getIsCanceled: () => boolean
): Promise<void> => {
  const payload = await sockClient.sendScopeStepAndWaitAsync(
    {
      type: ScopeType.STATE_DOWNLOAD,
    },
    {
      downloadTeams: [teamId],
    }
  );
  if (getIsCanceled() || payload === null) return;
  const blob = new Blob([JSON.stringify(payload)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const elem = document.createElement("a");
  elem.href = url;
  elem.download = "team-state.json";
  elem.style.display = "none";
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);
};
